import { useMemo, useState } from "react";
import { Badge, Box, Button, Card, Collapse, Group, Loader, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table, Tabs, Text, TextInput, Title } from "@mantine/core";
import { IconCode, IconSearch } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  defaultQuantity,
  fmtNumber,
  foodsFromResult,
  macrosFor,
  nutrientProfilePer100g,
  portionLabel,
  portionsFor,
  portionWeightG,
  previewGrams,
  type OutgoingSearchResult,
  type PassioFood,
  type PassioFoodResult,
  type PassioFoodResults,
  type SearchItemNutritionPreview,
} from "@passio/api-client";
import { passio } from "../lib/clients";
import { useClientHeaders } from "../lib/clientSettings";
import { useSession } from "../lib/session";
import {
  buildSample,
  CODE_LANGS,
  CODE_TOPICS,
  EXAMPLE_FOOD,
  expectedOutput,
  nutritionCode,
  outputLine,
  sampleNutrients,
  samplePortion,
  toApiNutrients,
  toApiPortion,
  type CodeLang,
  type CodeTopic,
  type SampleFood,
  type SampleInput,
} from "../lib/nutritionCode";
import { PageHeader } from "../theme/components";
import { ErrorNotice } from "../components/common";
import { FoodRow } from "../components/food";
import { CodeBlock, JsonView } from "../components/JsonView";

const EXAMPLES = ["banana", "peanut butter", "lasagna", "greek yogurt", "oat milk latte"];

const EXAMPLE_RESULT: PassioFoodResults = {
  results: [
    {
      displayName: EXAMPLE_FOOD.name,
      portions: EXAMPLE_FOOD.portions.map(toApiPortion),
      ingredients: [{ id: "example", name: EXAMPLE_FOOD.name, nutrients: toApiNutrients(EXAMPLE_FOOD), portions: EXAMPLE_FOOD.portions.map(toApiPortion) }],
    },
  ],
};

interface Picked {
  refCode: string;
  name: string;
  preview?: SearchItemNutritionPreview;
}

const ROWS: { key: "calories" | "protein" | "carbs" | "fat" | "fiber" | "sugar" | "sodium"; label: string; unit: string }[] = [
  { key: "calories", label: "Calories", unit: "kcal" },
  { key: "protein", label: "Protein", unit: "g" },
  { key: "carbs", label: "Carbs", unit: "g" },
  { key: "fat", label: "Fat", unit: "g" },
  { key: "fiber", label: "Fiber", unit: "g" },
  { key: "sugar", label: "Sugar", unit: "g" },
  { key: "sodium", label: "Sodium", unit: "mg" },
];

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <Card>
      <Group gap="sm" mb="sm" wrap="nowrap">
        <Badge circle size="lg" color="ink.8">
          {n}
        </Badge>
        <Title order={3} fz={18}>
          {title}
        </Title>
      </Group>
      {children}
    </Card>
  );
}

const mono = { fontFamily: "var(--mantine-font-family-monospace)" };

/** Portion picker, worked math and runnable code for one food (live or example). */
function FoodMath({ data, name, preview, example }: { data: PassioFoodResults; name: string; preview?: SearchItemNutritionPreview; example: boolean }) {
  const { header, foods } = useMemo(() => foodsFromResult(data), [data]);
  const portions = useMemo(() => portionsFor(header, foods), [header, foods]);
  const [pi, setPi] = useState(0);
  const [qty, setQty] = useState<number | null>(null);
  const [lang, setLang] = useState<CodeLang>("ts");
  const [topic, setTopic] = useState<CodeTopic>("portion");
  const [showJson, setShowJson] = useState(false);

  const portion = portions[Math.min(pi, portions.length - 1)];
  const quantity = qty ?? defaultQuantity(portion);
  const unitGrams = portionWeightG(portion);
  const grams = unitGrams * quantity;
  const profile = useMemo(() => nutrientProfilePer100g(foods), [foods]);
  const per100 = macrosFor(profile, 100);
  const m = macrosFor(profile, grams);
  const recipe = foods.length > 1;
  const displayName = (header as PassioFoodResult | undefined)?.displayName || (header as PassioFood | undefined)?.name || name;

  const sample = useMemo(() => {
    const profileRows = profile.map((e) => ({ amount: e.amount, nutrient: { name: e.name, shortName: e.shortName, unit: e.unit } }));
    const food: SampleFood = { name: displayName, nutrients: sampleNutrients(profileRows), portions: portions.map(samplePortion) };
    const input: SampleInput = { food, portionIndex: Math.min(pi, portions.length - 1), quantity };
    if (recipe) {
      input.recipe = {
        name: displayName,
        ingredients: foods.map((f) => ({
          name: (f as PassioFood).name || "Ingredient",
          nutrients: sampleNutrients(f.nutrients),
          portions: f.portions?.length ? f.portions.map(samplePortion) : [samplePortion(undefined)],
        })),
        portion: samplePortion(portion),
        quantity,
      };
    }
    if (preview?.portion?.weight?.value) {
      input.preview = {
        name: displayName,
        portion: { name: preview.portion.name || "serving", quantity: Number(preview.portion.quantity) || 1, unit: preview.portion.weight.unit || "g", value: Number(preview.portion.weight.value) },
        calories: Number(preview.calories) || 0,
        protein: Number(preview.protein) || 0,
        carbs: Number(preview.carbs) || 0,
        fat: Number(preview.fat) || 0,
      };
    }
    return buildSample(input);
  }, [profile, displayName, portions, pi, quantity, recipe, foods, portion, preview]);

  const usesLive: Record<CodeTopic, boolean> = { portion: !example, recipe: !example && recipe, preview: !example && Boolean(sample.preview && preview?.portion?.weight?.value), totals: !example };
  const pg = preview ? previewGrams(preview) : 0;
  const detailAtPreview = pg ? macrosFor(profile, pg) : null;
  const previewOff = detailAtPreview && preview?.calories ? Math.abs(detailAtPreview.calories - Number(preview.calories)) / Math.max(1, Number(preview.calories)) : null;

  const recipeRows = recipe
    ? foods.map((f) => {
        const p = f.portions?.[0];
        const g = portionWeightG(p) * defaultQuantity(p);
        const kcal100 = macrosFor(nutrientProfilePer100g([{ nutrients: f.nutrients }]), 100).calories;
        return { name: (f as PassioFood).name || "Ingredient", portion: p ? `${fmtNumber(defaultQuantity(p), 2)} × ${portionLabel(p)}` : "100 g (no portion)", g, kcal100, kcal: (kcal100 * g) / 100 };
      })
    : [];
  const recipeGrams = recipeRows.reduce((a, r) => a + r.g, 0);
  const recipeKcal = recipeRows.reduce((a, r) => a + r.kcal, 0);

  const usedJson = {
    portions: portions.map((p, i) => ({ ...p, ...(i === pi ? { "← selected": true } : {}) })),
    nutrients: sample.food.nutrients.map((n) => ({ amount: +n.amount.toFixed(3), nutrient: { name: n.name, shortName: n.shortName, unit: n.unit } })),
    ...(preview ? { nutritionPreview: preview } : {}),
  };

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Text fw={700} fz="lg">
          {displayName}
        </Text>
        {example ? (
          <Badge variant="light" color="gray">
            example data
          </Badge>
        ) : (
          <Badge variant="light" color="lime.8">
            live from the API
          </Badge>
        )}
        {recipe && (
          <Badge variant="light" color="violet">
            recipe · {foods.length} ingredients
          </Badge>
        )}
      </Group>

      <Step n={1} title="Portion to grams">
        <Group gap="sm" align="flex-end" wrap="wrap" mb="sm">
          <Select
            label="Portion (portions[])"
            data={portions.map((p, i) => ({ value: String(i), label: portionLabel(p) }))}
            value={String(Math.min(pi, portions.length - 1))}
            onChange={(v) => {
              setPi(Number(v ?? 0));
              setQty(null);
            }}
            allowDeselect={false}
            w={240}
          />
          <NumberInput label="Quantity" description="Defaults to suggestedQuantity[0]" value={quantity} onChange={(v) => setQty(Number(v) || 0)} min={0} step={portion?.name === "g" ? 10 : 0.5} decimalScale={2} w={170} />
        </Group>
        <Text fz="sm" style={mono}>
          grams = weight.value ({fmtNumber(Number(portion?.weight?.value) || 100, 2)} {portion?.weight?.unit || "g"}
          {unitGrams !== Number(portion?.weight?.value) ? ` = ${fmtNumber(unitGrams, 2)} g` : ""}) × quantity ({fmtNumber(quantity, 2)}) = <b>{fmtNumber(grams, 1)} g</b>
        </Text>
        <Text fz="xs" c="dimmed" mt={6}>
          The first portion is the default. Don&apos;t also multiply by portion.quantity: on full details it is the unit size (usually 1), not a serving count. No weight means 100 g.
        </Text>
      </Step>

      <Step n={2} title="Nutrients for that portion">
        <Text fz="sm" mb="xs" style={mono}>
          N = n (per 100 g) × {fmtNumber(grams, 1)} / 100
        </Text>
        <Table fz="sm" verticalSpacing={6} highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nutrient</Table.Th>
              <Table.Th ta="right">per 100 g</Table.Th>
              <Table.Th ta="right">× {fmtNumber(grams / 100, 3)}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ROWS.map((r) => (
              <Table.Tr key={r.key}>
                <Table.Td>{r.label}</Table.Td>
                <Table.Td ta="right" style={mono}>
                  {fmtNumber(per100[r.key], 2)} {r.unit}
                </Table.Td>
                <Table.Td ta="right" style={mono} fw={650}>
                  {fmtNumber(m[r.key], r.key === "calories" || r.key === "sodium" ? 0 : 1)} {r.unit}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Text fz="xs" c="dimmed" mt={6}>
          Match nutrients by shortName or name, and take energy in KCAL (some foods also list kJ). Round only for display.
        </Text>
      </Step>

      {recipe && (
        <Step n={3} title="Recipe: combine the ingredients">
          <Table fz="sm" verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ingredient</Table.Th>
                <Table.Th>Default portion</Table.Th>
                <Table.Th ta="right">Grams</Table.Th>
                <Table.Th ta="right">kcal / 100 g</Table.Th>
                <Table.Th ta="right">kcal</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recipeRows.map((r, i) => (
                <Table.Tr key={i}>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td fz="xs">{r.portion}</Table.Td>
                  <Table.Td ta="right" style={mono}>
                    {fmtNumber(r.g, 1)}
                  </Table.Td>
                  <Table.Td ta="right" style={mono}>
                    {fmtNumber(r.kcal100, 1)}
                  </Table.Td>
                  <Table.Td ta="right" style={mono}>
                    {fmtNumber(r.kcal, 1)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Text fz="sm" mt="sm" style={mono}>
            dish per 100 g = {fmtNumber(recipeKcal, 1)} kcal / {fmtNumber(recipeGrams, 1)} g × 100 = <b>{fmtNumber(per100.calories, 1)} kcal</b>; your {fmtNumber(grams, 1)} g = <b>{fmtNumber(m.calories)} kcal</b>
          </Text>
        </Step>
      )}

      {preview && (
        <Step n={recipe ? 4 : 3} title="Search preview vs full details">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Box p="sm" style={{ background: "var(--pd-cream)", borderRadius: 12 }}>
              <Text fz="xs" c="dimmed">
                nutritionPreview (show as-is)
              </Text>
              <Text fw={700}>
                {fmtNumber(Number(preview.calories))} kcal
              </Text>
              <Text fz="xs" style={mono}>
                {fmtNumber(Number(preview.portion?.quantity) || 1, 2)} × {preview.portion?.name || "serving"} ({fmtNumber(Number(preview.portion?.weight?.value), 1)} {preview.portion?.weight?.unit || "g"}) = {fmtNumber(pg, 1)} g
              </Text>
            </Box>
            <Box p="sm" style={{ background: "var(--pd-cream)", borderRadius: 12 }}>
              <Text fz="xs" c="dimmed">
                Full-detail math for the same {fmtNumber(pg, 1)} g
              </Text>
              <Text fw={700}>{detailAtPreview ? `${fmtNumber(detailAtPreview.calories)} kcal` : "–"}</Text>
              {previewOff !== null && (
                <Badge mt={4} size="sm" variant="light" color={previewOff <= 0.05 ? "lime.8" : "orange"}>
                  {previewOff <= 0.05 ? "matches" : `${Math.round(previewOff * 100)}% apart`}
                </Badge>
              )}
            </Box>
          </SimpleGrid>
          <Text fz="xs" c="dimmed" mt={6}>
            On a preview, portion.quantity is a count, so its grams are weight × quantity. Lists show the preview; once the user opens a food, switch to full details and the portion math above.
          </Text>
        </Step>
      )}

      <Group>
        <Button variant="subtle" color="gray" size="xs" leftSection={<IconCode size={14} />} onClick={() => setShowJson((s) => !s)}>
          {showJson ? "Hide" : "Show"} the response fields used
        </Button>
      </Group>
      <Collapse in={showJson}>
        <JsonView value={usedJson} expandDepth={3} maxHeight={360} />
      </Collapse>

      <Card>
        <Group justify="space-between" wrap="wrap" mb="sm" gap="sm">
          <Title order={3} fz={18}>
            Code
          </Title>
          <SegmentedControl size="xs" value={lang} onChange={(v) => setLang(v as CodeLang)} data={CODE_LANGS} />
        </Group>
        <Tabs value={topic} onChange={(v) => setTopic((v as CodeTopic) ?? "portion")} keepMounted={false}>
          <Tabs.List mb="sm">
            {CODE_TOPICS.map((t) => (
              <Tabs.Tab key={t.value} value={t.value}>
                {t.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {CODE_TOPICS.map((t) => (
            <Tabs.Panel key={t.value} value={t.value}>
              <Group gap="xs" mb="xs" wrap="nowrap" align="flex-start">
                <Text fz="sm" c="dimmed" style={{ flex: 1 }}>
                  {t.blurb}
                </Text>
                <Badge variant="light" color={usesLive[t.value] ? "lime.8" : "gray"} style={{ flex: "none" }}>
                  {usesLive[t.value] ? "your food" : "example data"}
                </Badge>
              </Group>
              <CodeBlock code={nutritionCode(t.value, lang, sample)} maxHeight={520} language={lang} />
              <Text fz="xs" c="dimmed" mt={6}>
                Prints <span style={mono}>{outputLine(expectedOutput(t.value, sample))}</span>
              </Text>
            </Tabs.Panel>
          ))}
        </Tabs>
        <Text fz="xs" c="dimmed" mt="sm">
          Every example is a complete program with no dependencies and no API key: it runs on the data above. The TypeScript and Python versions are tested against the demo&apos;s own helpers.
        </Text>
      </Card>
    </Stack>
  );
}

function LiveFood({ picked }: { picked: Picked }) {
  const headers = useClientHeaders();
  const q = useQuery({
    queryKey: ["food", picked.refCode, headers.toHeaders()],
    queryFn: async () => (await passio.foods.byRefCode(picked.refCode)).data,
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) {
    return (
      <Group p="xl" justify="center">
        <Loader color="violet" />
      </Group>
    );
  }
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data || !foodsFromResult(q.data).foods.length) return <Text c="dimmed">No nutrient details for this food.</Text>;
  return <FoodMath data={q.data} name={picked.name} preview={picked.preview} example={false} />;
}

export function NutritionMathPage() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { hasFeature } = useSession();
  const [term, setTerm] = useState("");
  const [picked, setPicked] = useState<Picked | null>(null);
  const [linked, setLinked] = useState<string | null>(null);
  const search = useMutation({ mutationFn: (t: string) => passio.foods.advancedSearch({ term: t, limit: 6 }) });

  const deepLink = pathname === "/nutrition-math" ? params.get("refCode") : null;
  if (deepLink && deepLink !== linked) {
    setLinked(deepLink);
    setPicked({ refCode: deepLink, name: params.get("name") || "Food" });
  }

  const run = (t = term) => {
    if (!t.trim()) return;
    setTerm(t);
    search.mutate(t.trim());
  };
  const results: OutgoingSearchResult[] = search.data?.data.results ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Client-side · after GET /napi/food/search/result/refCode/{refCode}"
        title="Nutrition math"
        description="The API returns building blocks: nutrients per 100 g, portions and search previews. Your app turns those into the numbers people see. Pick a food and every step and code example below uses its real data."
      />

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card>
          <Text fw={700} mb={4}>
            Lists: nutritionPreview
          </Text>
          <Text fz="sm" c="dimmed">
            Search, scan and meal extraction return a preview already scaled to a portion. Show it as-is; never apply the per-100 g formula to it.
          </Text>
        </Card>
        <Card>
          <Text fw={700} mb={4}>
            Details: nutrients per 100 g
          </Text>
          <Text fz="sm" c="dimmed">
            Hydrate a refCode for the full nutrient list. Every amount is per 100 g, so scale it by the grams of the portion the user picks.
          </Text>
        </Card>
      </SimpleGrid>

      {hasFeature("search") && (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              run();
            }}
          >
            <Group gap="sm" align="flex-end" wrap="nowrap">
              <TextInput style={{ flex: 1 }} label="Pick a food" placeholder="Search, e.g. banana or lasagna" value={term} onChange={(e) => setTerm(e.currentTarget.value)} />
              <Button type="submit" leftSection={<IconSearch size={16} />} loading={search.isPending}>
                Search
              </Button>
            </Group>
          </form>
          <Group gap={6} mt="sm">
            {EXAMPLES.map((x) => (
              <Badge key={x} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => run(x)}>
                {x}
              </Badge>
            ))}
            {picked && (
              <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setPicked(null)}>
                Back to example data
              </Button>
            )}
          </Group>
          {search.error && <ErrorNotice error={search.error} />}
          {results.length > 0 && (
            <Stack gap={2} mt="sm">
              {results.slice(0, 6).map((r, i) => (
                <FoodRow
                  key={r.refCode ?? i}
                  item={r}
                  onClick={r.refCode ? () => setPicked({ refCode: r.refCode!, name: r.displayName || r.shortName || "Food", preview: r.nutritionPreview }) : undefined}
                  right={
                    picked?.refCode === r.refCode ? (
                      <Badge size="sm" color="lime.8">
                        selected
                      </Badge>
                    ) : undefined
                  }
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      {picked ? <LiveFood key={picked.refCode} picked={picked} /> : <FoodMath key="example" data={EXAMPLE_RESULT} name={EXAMPLE_FOOD.name} example />}
    </Stack>
  );
}
